/**
 * Componente: Modal de Selección de Estado o Departamento — EstadoModalSelector
 * Fiel al diseño de /root/Avifacil_Planeacion_proyecto/desing frontend/desing-modal-estado.html
 * - Centrado en pantalla con fondo blur neutro esmerilado (no morado).
 * - Alimentado por `country-state-city` a través del servicio geo.
 * - Búsqueda en tiempo real, scrollbar lila/violeta y checkmark de selección.
 */

'use client';

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './EstadoModalSelector.module.css';

export interface EstadoItem {
  value: string;
  label: string;
  isoCode?: string;
}

export interface EstadoModalSelectorProps {
  isOpen: boolean;
  selectedStateName?: string;
  countryName?: string;
  states: EstadoItem[];
  isLoading?: boolean;
  onSelect: (stateName: string) => void;
  onClose: () => void;
  onOpenCountryModal?: () => void;
}

export function EstadoModalSelector({
  isOpen,
  selectedStateName,
  countryName,
  states,
  isLoading = false,
  onSelect,
  onClose,
  onOpenCountryModal,
}: EstadoModalSelectorProps) {
  const [search, setSearch] = useState('');
  const [mounted, setMounted] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Filtrado reactivo en tiempo real
  const filteredStates = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return states;

    return states.filter((s) => {
      const name = (s.label || s.value || '').toLowerCase();
      const code = (s.isoCode || '').toLowerCase();
      return name.includes(query) || code === query;
    });
  }, [states, search]);

  // Manejo de foco automático al abrir
  useEffect(() => {
    if (isOpen) {
      setSearch('');
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Cerrar con tecla Escape
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !mounted) return null;

  const normalizedSelected = (selectedStateName || '').trim().toLowerCase();

  return createPortal(
    <div
      className={styles.backdrop}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="state-modal-title"
    >
      <div
        className={styles.modalCard}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Botón Cerrar (X) */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 sm:top-6 sm:right-6 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 flex items-center justify-center transition-colors cursor-pointer focus:outline-none z-10"
          aria-label="Cerrar modal"
        >
          <span className="material-symbols-outlined text-[18px]">close</span>
        </button>

        {/* Encabezado: Icono Badge y Título */}
        <div className="flex items-center gap-3 pr-10">
          <div className={styles.iconBadge}>
            <span className="material-symbols-outlined text-[22px] sm:text-[24px]">location_on</span>
          </div>
          <div>
            <h2
              id="state-modal-title"
              className="text-lg sm:text-2xl font-bold text-gray-900 leading-tight"
            >
              Selecciona tu estado o departamento
            </h2>
            {countryName && (
              <p className="text-xs text-gray-500 mt-0.5">
                Mostrando departamentos de{' '}
                <span className="font-semibold text-purple-700">{countryName}</span>
              </p>
            )}
          </div>
        </div>

        {/* Barra de Búsqueda con Iconos Blindados contra Colapso */}
        <div className={styles.searchContainer}>
          <div className={styles.searchIconWrapper}>
            <span className="material-symbols-outlined text-[20px] pointer-events-none select-none">
              search
            </span>
          </div>
          <input
            ref={searchInputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar estado o departamento..."
            className={styles.searchInput}
          />
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                searchInputRef.current?.focus();
              }}
              className={styles.clearSearchBtn}
              aria-label="Borrar búsqueda"
              title="Borrar búsqueda"
            >
              <svg
                className="w-3.5 h-3.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.5"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Grilla de Estados en 2 Columnas con Scroll Personalizado (#c084fc / #f3e8ff) */}
        <div
          data-scrollable="true"
          className={`w-full flex-1 ${styles.stateListScroll}`}
        >
          {isLoading ? (
            <div className="py-12 flex flex-col items-center justify-center text-gray-400 gap-2">
              <span className="material-symbols-outlined animate-spin text-3xl text-purple-600">
                progress_activity
              </span>
              <p className="text-sm font-medium">Cargando estados y departamentos...</p>
            </div>
          ) : filteredStates.length > 0 ? (
            <div className={styles.stateGrid}>
              {filteredStates.map((st) => {
                const stateName = st.label || st.value;
                const isSelected = normalizedSelected === stateName.toLowerCase();

                return (
                  <button
                    key={`${st.value}-${st.isoCode || ''}`}
                    type="button"
                    onClick={() => {
                      onSelect(stateName);
                      onClose();
                    }}
                    className={`${styles.stateButton} ${
                      isSelected ? styles.stateButtonSelected : ''
                    }`}
                  >
                    <span className="truncate pr-2">{stateName}</span>
                    {isSelected && (
                      <span className="material-symbols-outlined text-[18px] text-[#9333ea] font-bold flex-shrink-0">
                        check
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ) : states.length === 0 ? (
            <div className="py-10 text-center text-gray-500">
              <span className="material-symbols-outlined text-4xl text-gray-300 mb-2">
                travel_explore
              </span>
              <p className="text-sm font-semibold text-gray-800">
                No encontramos estados predefinidos para {countryName || 'este país'}
              </p>
              <p className="text-xs text-gray-400 mt-1 max-w-xs mx-auto">
                Puedes escribir el nombre de tu departamento directamente o cambiar el país seleccionado.
              </p>
              {search.trim() && (
                <button
                  type="button"
                  onClick={() => {
                    onSelect(search.trim());
                    onClose();
                  }}
                  className="mt-4 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-semibold shadow-xs cursor-pointer transition-colors"
                >
                  Usar &quot;{search.trim()}&quot; como mi departamento
                </button>
              )}
              {onOpenCountryModal && (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onOpenCountryModal();
                  }}
                  className="block mx-auto mt-3 text-xs font-semibold text-[#c25ee8] hover:underline cursor-pointer"
                >
                  Cambiar país
                </button>
              )}
            </div>
          ) : (
            <div className="py-12 text-center text-gray-500">
              <span className="material-symbols-outlined text-4xl text-gray-300 mb-2">
                search_off
              </span>
              <p className="text-sm font-medium">
                No encontramos ningún estado o departamento que coincida con &quot;{search}&quot;
              </p>
              {search.trim() && (
                <button
                  type="button"
                  onClick={() => {
                    onSelect(search.trim());
                    onClose();
                  }}
                  className="mt-3 inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 rounded-xl text-xs font-semibold cursor-pointer transition-colors"
                >
                  <span className="material-symbols-outlined text-sm">add</span>
                  <span>Usar &quot;{search.trim()}&quot;</span>
                </button>
              )}
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="text-xs font-semibold text-[#c25ee8] hover:underline cursor-pointer"
                >
                  Ver todos los departamentos
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
